import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SEARCH_TYPES, BUDGET_RANGES } from "../lib/constants";

const inputClass =
  "w-full bg-transparent border-b border-white/20 focus:border-gold text-white placeholder-white/30 py-3 font-sans text-sm outline-none transition-colors duration-300";

export const RequestDialog = ({ open, onOpenChange, kind = "asset_inquiry", asset = null, title, subtitle }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", search_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/requests", {
        ...form,
        kind,
        asset_id: asset?.id || null,
        asset_type: asset?.type || null,
        search_type: form.search_type || asset?.category || "",
      });
      toast.success("Request received. Our team will contact you confidentially.");
      setForm({ name: "", email: "", phone: "", search_type: "", budget: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="request-dialog"
        >
          <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg glass rounded-2xl p-8 md:p-10 max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={() => onOpenChange(false)}
              data-testid="request-dialog-close"
              className="absolute top-5 right-5 text-white/60 hover:text-gold transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="font-sans text-gold text-[10px] tracking-luxury uppercase mb-3">Private Enquiry</p>
            <h3 className="font-serif text-white text-3xl md:text-4xl mb-2">{title || "Request Details"}</h3>
            <p className="font-sans text-white/50 text-sm mb-8">
              {subtitle || (asset ? `${asset.type} — ${asset.country}` : "Confidential. Handled with absolute discretion.")}
            </p>

            <form onSubmit={submit} className="space-y-5">
              <input data-testid="req-name" className={inputClass} placeholder="Full name" value={form.name} onChange={update("name")} />
              <input data-testid="req-email" type="email" className={inputClass} placeholder="Email address" value={form.email} onChange={update("email")} />
              <input data-testid="req-phone" className={inputClass} placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} />
              <div className="grid grid-cols-2 gap-5">
                <select data-testid="req-type" className={`${inputClass} text-white/70`} value={form.search_type} onChange={update("search_type")}>
                  <option value="" className="bg-navy">Type of search</option>
                  {SEARCH_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-navy">{t}</option>
                  ))}
                </select>
                <select data-testid="req-budget" className={`${inputClass} text-white/70`} value={form.budget} onChange={update("budget")}>
                  <option value="" className="bg-navy">Budget</option>
                  {BUDGET_RANGES.map((b) => (
                    <option key={b} value={b} className="bg-navy">{b}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="req-message" rows={3} className={inputClass} placeholder="Message" value={form.message} onChange={update("message")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="req-submit"
                className="w-full bg-gold text-navy py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Submit Private Request"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
